import React, { useEffect, useState, useContext } from "react";
import QRCode from "qrcode.react";
import toastError from "../../errors/toastError";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  Typography,
  IconButton,
  CircularProgress,
} from "@material-ui/core";
import { X as LucideX, Smartphone, Wifi } from "lucide-react";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";

import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => {
  const ds = theme.chatiaDS || {};
  const dsColors = ds.colors || {};
  const isDark = theme.palette.type === "dark";
  return {
    dialogPaper: {
      borderRadius: "21px 21px 12px 12px !important",
      maxHeight: "85vh !important",
      overflow: "hidden !important",
      background: isDark ? "#121212" : "#ffffff",
    },
    header: {
      ...(ds.header || {}),
      flexShrink: 0,
    },
    headerTitle: {
      ...(ds.headerTitle || {}),
    },
    closeButton: {
      backgroundColor: ds.closeButton?.backgroundColor || "rgba(244, 67, 54, 0.1)",
      color: ds.closeButton?.color || "#f44336",
      transition: ds.closeButton?.transition || "all 0.2s ease",
    },
    content: {
      padding: "24px 28px",
      display: "flex",
      gap: 24,
      alignItems: "stretch",
    },
    leftCol: {
      flex: 1,
      minWidth: 260,
      display: "flex",
      flexDirection: "column",
      gap: 14,
    },
    intro: {
      fontSize: 13.5,
      lineHeight: 1.55,
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
    },
    stepList: {
      display: "flex",
      flexDirection: "column",
      gap: 10,
      margin: 0,
      padding: 0,
      listStyle: "none",
    },
    stepItem: {
      display: "flex",
      alignItems: "flex-start",
      gap: 12,
      fontSize: 13,
      lineHeight: 1.5,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
    },
    stepNum: {
      flexShrink: 0,
      width: 22,
      height: 22,
      borderRadius: "50%",
      background: theme.palette.primary.main,
      color: "#fff",
      fontSize: 12,
      fontWeight: 700,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
    },
    stepText: {
      "& strong": {
        fontWeight: 600,
      },
    },
    statusBadge: {
      marginTop: 4,
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "8px 12px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 600,
      background: isDark ? "rgba(33,150,243,0.10)" : "rgba(33,150,243,0.08)",
      color: isDark ? "#90caf9" : "#1565c0",
      border: `1px solid ${isDark ? "rgba(33,150,243,0.25)" : "rgba(33,150,243,0.20)"}`,
      width: "fit-content",
    },
    qrWrap: {
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: 320,
      height: 320,
      padding: 16,
      borderRadius: 16,
      background: "#ffffff",
      border: `1px solid ${dsColors.border || (isDark ? "#202632" : "#e8e8e8")}`,
      boxShadow: isDark
        ? "0 4px 16px rgba(0,0,0,0.4)"
        : "0 4px 16px rgba(15,23,42,0.08)",
    },
    qrLoadingWrap: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 12,
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      fontSize: 13,
    },
  };
});

const QrcodeModal = ({ open, onClose, whatsAppId }) => {
  const classes = useStyles();
  const [qrCode, setQrCode] = useState("");
  const { user, socket } = useContext(AuthContext);

  useEffect(() => {
    const fetchSession = async () => {
      if (!whatsAppId) return;

      try {
        const { data } = await api.get(`/whatsapp/${whatsAppId}`);
        setQrCode(data.qrcode);
      } catch (err) {
        toastError(err);
      }
    };
    fetchSession();
  }, [whatsAppId]);

  useEffect(() => {
    if (!whatsAppId) return;
    const companyId = user.companyId;

    const onWhatsappData = (data) => {
      if (data.action === "update" && data.session.id === whatsAppId) {
        setQrCode(data.session.qrcode);
      }

      if (data.action === "update" && data.session.qrcode === "") {
        onClose();
      }
    };
    socket.on(`company-${companyId}-whatsappSession`, onWhatsappData);

    return () => {
      socket.off(`company-${companyId}-whatsappSession`, onWhatsappData);
    };
  }, [whatsAppId, onClose]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      scroll="paper"
      PaperProps={{ className: classes.dialogPaper }}
    >
      <div className={classes.header}>
        <Typography className={classes.headerTitle}>
          {i18n.t("qrcodeModal.title") || "Conectar WhatsApp"}
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          className={classes.closeButton}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.18)";
            e.currentTarget.style.color = "#ffffff";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.08)";
            e.currentTarget.style.color = "#ffffff";
          }}
        >
          <LucideX size={18} strokeWidth={2.5} />
        </IconButton>
      </div>

      <div className={classes.content}>
        <div className={classes.leftCol}>
          <div className={classes.intro}>
            {i18n.t("qrcodeModal.intro") ||
              "Use o WhatsApp do celular para escanear o código ao lado e conectar esta sessão."}
          </div>

          <ol className={classes.stepList}>
            <li className={classes.stepItem}>
              <span className={classes.stepNum}>1</span>
              <span
                className={classes.stepText}
                dangerouslySetInnerHTML={{
                  __html:
                    i18n.t("qrcodeModal.step1") ||
                    "Abra o <strong>WhatsApp</strong> no seu celular.",
                }}
              />
            </li>
            <li className={classes.stepItem}>
              <span className={classes.stepNum}>2</span>
              <span
                className={classes.stepText}
                dangerouslySetInnerHTML={{
                  __html:
                    i18n.t("qrcodeModal.step2") ||
                    "Toque em <strong>Mais opções</strong> (Android) ou <strong>Ajustes</strong> (iPhone).",
                }}
              />
            </li>
            <li className={classes.stepItem}>
              <span className={classes.stepNum}>3</span>
              <span
                className={classes.stepText}
                dangerouslySetInnerHTML={{
                  __html:
                    i18n.t("qrcodeModal.step3") ||
                    "Selecione <strong>Aparelhos conectados</strong> e depois <strong>Conectar um aparelho</strong>.",
                }}
              />
            </li>
            <li className={classes.stepItem}>
              <span className={classes.stepNum}>4</span>
              <span
                className={classes.stepText}
                dangerouslySetInnerHTML={{
                  __html:
                    i18n.t("qrcodeModal.step4") ||
                    "Aponte a câmera do celular para o QR Code ao lado.",
                }}
              />
            </li>
          </ol>

          <div className={classes.statusBadge}>
            <Wifi size={14} strokeWidth={2.2} />
            {qrCode
              ? i18n.t("qrcodeModal.statusReady") ||
                "Pronto para escanear"
              : i18n.t("qrcodeModal.waiting") || "Aguardando QR Code..."}
          </div>
        </div>

        <div className={classes.qrWrap}>
          {qrCode ? (
            <QRCode value={qrCode} size={288} level="M" />
          ) : (
            <div className={classes.qrLoadingWrap}>
              <CircularProgress size={36} />
              <Smartphone size={18} strokeWidth={1.8} />
              <span>{i18n.t("qrcodeModal.waiting") || "Aguardando QR Code..."}</span>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default React.memo(QrcodeModal);
